import type { ReactNode } from 'react';
import { CheckCircle2, Sparkles, XCircle } from 'lucide-react';
import { Badge } from '@/shared/components/shadcn/badge';
import { cn } from '@/shared/utils';
import type { InsightCard as InsightCardType } from '../types';

interface Props {
  insight: InsightCardType;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggle?: () => void;
  action?: ReactNode;
  expandedContent?: ReactNode;
  isRecommended?: boolean;
  recommendedReason?: string;
}

export function InsightCard({ insight, isSelectable, isSelected, onToggle, action, expandedContent, isRecommended, recommendedReason }: Props) {
  const isDo = insight.type === 'do';

  return (
    <div
      onClick={isSelectable ? onToggle : undefined}
      className={cn(
        'flex flex-col gap-3 rounded-xl border bg-card p-5 transition-all',
        isDo ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-rose-500',
        isSelectable && 'cursor-pointer select-none',
        isSelectable && isSelected && 'ring-2 ring-primary ring-offset-1',
        isSelectable && !isSelected && 'hover:bg-muted/30',
      )}
    >
      {isRecommended && (
        <div className="flex flex-col gap-1.5">
          <div className="inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1" style={{ backgroundColor: '#E9FF41' }}>
            <Sparkles className="size-3" style={{ color: '#1a2200' }} />
            <span className="text-xs font-semibold" style={{ color: '#1a2200' }}>Recommended</span>
          </div>
          {recommendedReason && (
            <p className="text-xs leading-relaxed text-muted-foreground">{recommendedReason}</p>
          )}
        </div>
      )}
      <div className="flex items-start gap-3">
        {isSelectable && (
          <div
            className={cn(
              'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors',
              isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40',
            )}
          >
            {isSelected && (
              <svg className="size-2.5 text-primary-foreground" viewBox="0 0 10 10" fill="none">
                <path
                  d="M2 5l2.5 2.5 3.5-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        )}
        <div className={cn('mt-0.5 shrink-0', isSelectable && 'hidden')}>
          {isDo ? (
            <CheckCircle2 className="size-5 text-emerald-500" />
          ) : (
            <XCircle className="size-5 text-rose-500" />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span
            className={cn(
              'text-xs font-bold uppercase tracking-wider',
              isDo ? 'text-emerald-600' : 'text-rose-600',
            )}
          >
            {isDo ? 'DO' : "DON'T"}
          </span>
          <p className="text-sm font-semibold leading-snug text-foreground">{insight.title}</p>
        </div>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">{insight.detail}</p>

      {(insight.tags.length > 0 || action) && (
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1.5 flex-1">
            {insight.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs font-normal">
                {tag}
              </Badge>
            ))}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {expandedContent}
    </div>
  );
}
